import { Checkbox, Dropdown, Label, Stack, TextField } from '@fluentui/react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import {
	FieldType,
	IEntityColumn,
	IFormField,
} from '../../API';
import { IUserConfigPackage } from '../../API';
import DatePickerNullable from '../controls/DatePickerNullable';
import PeoplePickerCtrl from '../controls/PeoplePickerCtrl';
import EntitySelectorCtrl, {
	EntitySelectorCtrlState,
	EntitySelectorMode,
} from '../entitySelector/EntitySelectorCtrl';
import { EntityDetailsState } from '../controls/EntityDetails';

export interface IFormFieldProps {
	state: FormFieldState;
}

export class FormFieldState {
	userConfig: IUserConfigPackage;
	info: IFormField;
	col?: IEntityColumn;

	value?: any;
	entityType: string;
	entityId: string;

	entitySelector?: EntitySelectorCtrlState;

	constructor(
		userConfig: IUserConfigPackage,
		entityType: string,
		entityId: string,
		field: IFormField,
		cols: IEntityColumn[],
		detailsState: EntityDetailsState
	) {
		this.userConfig = userConfig;
		this.info = field;
		this.entityType = entityType;
		this.entityId = entityId;
		this.col = cols.find((c) => c.id === field.id);

		if (this.col?.fieldType === FieldType.Lookup && !!this.col?.lookupType) {
			this.entitySelector = new EntitySelectorCtrlState(
				this.col?.lookupType,
				userConfig,
				EntitySelectorMode.Picker,
				detailsState
			);

			this.entitySelector.multi = this.col?.multi;
			this.entitySelector.onSelectionChange((items) => {
				this.setValueInternal(items.map((i) => i.Id));
			});
		}

		makeAutoObservable(this);
	}

	public setValue(value: any) {
		this.value = value;

		//If it's a lookup, set the lookup control
		this.entitySelector?.setSelectedItemIds(value);
	}

	public setValueInternal(value: string[]) {
		this.value = value;
	}
}

class FormField extends React.PureComponent<IFormFieldProps> {
	public render() {
		return (
			<Stack>
				{this.shouldRenderTitleLabel() ? (
					<Label>{this.props.state.info.title}</Label>
				) : (
					<></>
				)}
				{this.getField()}
			</Stack>
		);
	}

	shouldRenderTitleLabel() {
		return this.props.state.col?.fieldType !== FieldType.Boolean;
	}

	getField() {
		let fieldType = this.props.state.col?.fieldType;

		if (fieldType === undefined) {
			return this.textField(false);
		}

		switch (fieldType) {
			case FieldType.Text:
				return this.textField(false);
			case FieldType.Note:
				return this.textField(true);
			case FieldType.Choice:
			case FieldType.MultiChoice:
				return this.choiceField();
			case FieldType.CustomFieldType:
				return this.customFieldType();
			case FieldType.DateTime:
				return this.dateField();
			case FieldType.Lookup:
				return this.lookup();
			case FieldType.Boolean:
				return this.boolean();
			case FieldType.Number:
			case FieldType.Integer:
				return this.number();
			case FieldType.User:
				return this.people();
		}
	}

	number() {
		return (
			<TextField
				type="number"
				value={this.props.state.value || ''}
				onChange={(_, v) => {
					this.props.state.setValue(v);
				}}
			/>
		);
	}

	people() {
		let value = FormField.getPeopleIds(this.props.state.value);
		return (
			<PeoplePickerCtrl
				selectedIds={value}
				onSelectionChanged={(ids) => {
					this.props.state.setValue(ids);
				}}
			/>
		);
	}

	boolean() {
		let checked: boolean = FormField.getBoolean(this.props.state.value);

		return (
			<div style={{ marginTop: 8 }}>
				<Checkbox
					defaultChecked={checked}
					label={this.props.state.info.title}
					onChange={(_, checked) => {
						this.props.state.setValue(!!checked);
					}}
				/>
			</div>
		);
	}

	lookup() {
		if (this.props.state.entitySelector) {
			return <EntitySelectorCtrl state={this.props.state.entitySelector} />;
		}
	}

	dateField() {
		let date = this.props.state.value ? new Date(this.props.state.value) : null;

		return (
			<DatePickerNullable
				userConfig={this.props.state.userConfig}
				value={date}
				label={''}
				onSelectDate={(d) => {
					this.props.state.setValue(d);
				}}
			/>
		);
	}

	customFieldType() {
		return (
			<Dropdown
				defaultSelectedKey={Number(this.props.state.value) || FieldType.Text}
				onChange={(_, o) => {
					this.props.state.setValue(o?.key as FieldType);
				}}
				options={[
					{ key: FieldType.Text, text: 'Text' },
					{ key: FieldType.DateTime, text: 'Date' },
					{ key: FieldType.User, text: 'People' },
					{ key: FieldType.Boolean, text: 'Yes/No' },
					{ key: FieldType.Number, text: 'Number' },
					{ key: FieldType.Choice, text: 'Choice' },
				]}
			></Dropdown>
		);
	}

	choiceField() {
		let value = (this.props.state.value as string) || '';

		let selectedOptions: string[] = FormField.getChoices(value);

		let multi = !!this.props.state.col?.multi;
		let options = (this.props.state.col?.choices || [value]).map((c) => {
			return { key: c, text: c };
		});

		return (
			<Dropdown
				multiSelect={multi}
				defaultSelectedKey={multi ? selectedOptions : selectedOptions[0] || ''}
				options={options}
				onChange={(_, o) => {
					if (multi) {
						//This option is now either selected or unselected - add or remove it
						if (o?.selected) {
							selectedOptions = selectedOptions.concat([o.key as string]);
						} else {
							selectedOptions = selectedOptions.filter((c) => c !== o?.key);
						}

						this.props.state.setValue(selectedOptions.join(';#'));
					} else {
						this.props.state.setValue(o?.key as string);
					}
				}}
			/>
		);
	}

	textField(multiline: boolean) {
		return (
			<TextField
				multiline={multiline}
				rows={multiline ? 8 : undefined}
				autoComplete="off"
				placeholder={`Enter value...`}
				value={(this.props.state.value as string) || ''}
				onChange={(_, t) => {
					this.props.state.setValue(t || '');
				}}
			></TextField>
		);
	}

	public static getChoices(internalValue: string): string[] {
		if (Array.isArray(internalValue)) {
			return internalValue || [];
		} else {
			return (internalValue || '')
				.split(';#')
				.map((p) => p.trim())
				.filter((p) => !!p);
		}
	}

	public static getPeopleIds(internalValue: string | string[]): string[] {
		if (!internalValue) {
			return [];
		}

		if (typeof internalValue === 'string') {
			return (internalValue || '')
				.split(',')
				.map((p) => p.trim())
				.filter((p) => !!p);
		} else if (internalValue.length) {
			return internalValue as string[];
		} else {
			return [];
		}
	}

	public static getBoolean(internalValue: string): boolean {
		if (typeof internalValue === 'boolean') {
			return internalValue;
		}

		internalValue = (internalValue || '').toString().toLowerCase();
		return internalValue === 'true' || internalValue === '1';
	}
}

export default observer(FormField);
